import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Construction, Sparkles, Menu, X } from 'lucide-react';
import Footer from '../components/Footer';
import TiccAIlogo from '../assets/TiccAIlogo.png';
import AuthModal from '../components/AuthModal';

const Pricing = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<false | 'signin' | 'signup'>(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Helper to navigate home and scroll to a section after navigation
  const goHomeAndScroll = (sectionId: string, yOffset: number) => {
    navigate('/');
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100); // Delay to allow navigation
  };

  const openAuthModal = (mode: 'signin' | 'signup') => setIsAuthModalOpen(mode);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  return (
    <div className="min-h-screen bg-[#09101c] flex flex-col relative overflow-x-hidden">
      {/* Animated radial gradients and grid overlay for depth, matching App.tsx */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] pointer-events-none z-0">
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            background: `
              radial-gradient(circle at 45% 0%, rgba(139,  92, 246, 0.30)  0%, rgba(101,  62, 200, 0.20) 20%, rgba( 50,  35, 120, 0.12) 40%, rgba(  9,  16,  28, 0.06) 60%, transparent 80%)
            `,
            filter: 'blur(0px)',
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        />
      </div>
      {/* Animated particles background */}
      <ParticlesBackground />
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-20 -left-80 w-[600px] h-[600px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(255, 0, 128, 0.1), transparent)', filter: 'blur(100px)', transform: 'translateZ(0)', willChange: 'transform'}} />
        <div className="absolute top-[20%] right-[10%] w-[400px] h-[400px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08), transparent)', filter: 'blur(80px)', animationDuration: '8s'}} />
        <div className="absolute top-[60%] left-[5%] w-[500px] h-[500px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08), transparent)', filter: 'blur(90px)', animationDuration: '10s'}} />
        <div className="absolute top-[40%] right-[15%] w-[300px] h-[300px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(67, 56, 202, 0.07), transparent)', filter: 'blur(70px)', animationDuration: '12s'}} />
        <div className="absolute top-[80%] right-[20%] w-[450px] h-[450px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(79, 70, 229, 0.08), transparent)', filter: 'blur(85px)', animationDuration: '9s'}} />
      </div>
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.225] z-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
        }}
      />

      {/* Navbar (copied from App.tsx HomePage) */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="mx-4 sm:mx-6 lg:mx-8 mt-4">
          <nav className="max-w-7xl mx-auto backdrop-blur-[6px] bg-[#09101c]/40 rounded-2xl border border-white/[0.1] font-sans">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => navigate('/')}
                      className="flex items-center gap-2"
                    >
                      <img src={TiccAIlogo} alt="TiccAI" className="h-7 w-7" />
                      <span className="text-2xl font-bold text-white">TiccAI</span>
                    </button>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="flex items-center space-x-8">
                    <button 
                      onClick={() => goHomeAndScroll('features', -50)}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Features</span>
                    </button>
                    <button 
                      onClick={() => goHomeAndScroll('how-it-works', 20)}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Dashboard</span>
                    </button>
                    <button 
                      onClick={() => goHomeAndScroll('integrations', -100)}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Integrations</span>
                    </button>
                    <button 
                      onClick={() => openAuthModal('signin')}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Sign in</span>
                    </button>
                    <button 
                      onClick={() => openAuthModal('signup')}
                      className="get-started-button bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full font-sans"
                    >
                      <span>Get started</span>
                    </button>
                  </div>
                </div>
                <div className="md:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="text-gray-300 hover:text-white font-sans"
                  >
                    {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </button>
                </div>
              </div>
              {isMobileMenuOpen && (
                <div className="md:hidden pb-4">
                  <div className="px-2 space-y-1">
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); goHomeAndScroll('features', -50); }}
                      className="nav-button block text-gray-300 py-2 font-sans"
                    >
                      <span>Features</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); goHomeAndScroll('how-it-works', 20); }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Dashboard</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); goHomeAndScroll('integrations', -100); }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Integrations</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); openAuthModal('signin'); }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Sign in</span>
                    </button>
      <button
                      onClick={() => { setIsMobileMenuOpen(false); openAuthModal('signup'); }}
                      className="get-started-button w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full mt-4 font-sans"
      >
                      <span>Get started</span>
      </button>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      <main className="flex-1 relative z-10 py-16 px-4 mt-12">
        <div className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent animate-gradient">
              Support that grows with you
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Automate ticket management so your engineers can focus on higher-value projects.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Basic Plan */}
            <div className="relative group rounded-xl p-[2px] transition-all duration-500 hover:scale-[1.025] hover:shadow-2xl">
              <div className="absolute inset-0 rounded-xl pointer-events-none z-0 transition-all duration-500 group-hover:opacity-100 opacity-80"
                style={{
                  background: 'linear-gradient(135deg, rgba(236,72,153,0.18) 0%, rgba(139,92,246,0.14) 100%)',
                  boxShadow: '0 0 24px 2px rgba(236,72,153,0.10), 0 0 0 1.5px rgba(255,255,255,0.08)'
                }}
              />
              <div className="relative bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm rounded-xl border border-white/[0.08] p-8 z-10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-500/50 text-white px-4 py-1 rounded-full text-sm flex items-center gap-2 transition-transform group-hover:scale-110 backdrop-blur-xl">
                <Construction className="h-4 w-4" />
                <span>Coming Soon</span>
              </div>
              <div className="opacity-60">
                <h2 className="text-2xl font-bold text-white mb-4">Basic</h2>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">$50</span>
                  <span className="text-gray-400">/mo per service</span>
                </div>
                <p className="text-gray-400 mb-8">For teams just starting their automation journey.</p>
                <button disabled className="w-full bg-white/[0.05] text-white py-2 px-4 rounded-lg cursor-not-allowed mb-8 backdrop-blur-xl">
                  Coming Soon
                </button>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Up to 50 tickets/month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Basic AI resolution</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Email integration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Standard support</span>
                  </li>
                </ul>
                </div>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="relative group rounded-xl p-[2px] transition-all duration-500 hover:scale-[1.025] hover:shadow-2xl">
              <div className="absolute inset-0 rounded-xl pointer-events-none z-0 transition-all duration-500 group-hover:opacity-100 opacity-80"
                style={{
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(236,72,153,0.14) 100%)',
                  boxShadow: '0 0 24px 2px rgba(139,92,246,0.10), 0 0 0 1.5px rgba(255,255,255,0.08)'
                }}
              />
              <div className="relative bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm rounded-xl border border-white/[0.08] p-8 z-10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-500/50 text-white px-4 py-1 rounded-full text-sm flex items-center gap-2 transition-transform group-hover:scale-110 backdrop-blur-xl">
                <Construction className="h-4 w-4" />
                <span>Coming Soon</span>
              </div>
              <div className="opacity-60">
                <h2 className="text-2xl font-bold text-white mb-4">Pro</h2>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">$100</span>
                  <span className="text-gray-400">/mo per service</span>
                </div>
                <p className="text-gray-400 mb-8">For growing teams with advanced needs.</p>
                <button disabled className="w-full bg-white/[0.05] text-white py-2 px-4 rounded-lg cursor-not-allowed mb-8 backdrop-blur-xl">
                  Coming Soon
                </button>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Everything in Basic, plus:</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Up to 200 tickets/month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Advanced AI resolution</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Slack & Teams integration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-pink-500 mt-0.5" />
                    <span className="text-gray-400">Priority support</span>
                  </li>
                </ul>
                </div>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="relative group rounded-xl p-[2.5px] transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl">
              <div className="absolute inset-0 rounded-xl pointer-events-none z-0 transition-all duration-500 group-hover:opacity-100 opacity-100"
                style={{
                  background: 'linear-gradient(120deg, rgba(236,72,153,0.55) 0%, rgba(168,85,247,0.45) 50%, rgba(59,130,246,0.38) 100%)',
                  boxShadow: '0 0 48px 8px rgba(236,72,153,0.22), 0 0 0 2.5px rgba(236,72,153,0.38), 0 0 0 6px rgba(168,85,247,0.10)'
                }}
              />
              <div className="relative bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm rounded-xl border-2 border-transparent p-8 z-10" style={{
                boxShadow: '0 0 0 1.5px rgba(236,72,153,0.25), 0 0 32px 6px rgba(168,85,247,0.10)'
              }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-pink-500/40 to-purple-500/40 text-white px-4 py-1 rounded-full text-sm transition-transform group-hover:scale-110 backdrop-blur-xl flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                <span>Custom Plan</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4 transition-colors group-hover:text-pink-500">Enterprise</h2>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">Custom</span>
                <span className="text-gray-400"> pricing</span>
              </div>
              <p className="text-gray-400 mb-8">For large teams with custom requirements.</p>
              <a 
                href="https://cal.com/ticcai/30m"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-pink-500/70 to-purple-500/70 text-white py-2 px-4 rounded-lg hover:from-pink-600 hover:to-purple-600 transition-all duration-300 mb-8 text-center relative overflow-hidden group/button backdrop-blur-xl"
              >
                <span className="relative z-10">Book Demo</span>
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover/button:opacity-100 transition-opacity duration-300"></div>
              </a>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 transition-transform group-hover:translate-x-1">
                  <Check className="h-5 w-5 text-pink-500 mt-0.5 transition-colors group-hover:text-pink-400" />
                  <span className="text-gray-400 transition-colors group-hover:text-gray-300">Everything in Pro, plus:</span>
                </li>
                <li className="flex items-start gap-3 transition-transform group-hover:translate-x-1">
                  <Check className="h-5 w-5 text-pink-500 mt-0.5 transition-colors group-hover:text-pink-400" />
                  <span className="text-gray-400 transition-colors group-hover:text-gray-300">Unlimited tickets</span>
                </li>
                <li className="flex items-start gap-3 transition-transform group-hover:translate-x-1">
                  <Check className="h-5 w-5 text-pink-500 mt-0.5 transition-colors group-hover:text-pink-400" />
                  <span className="text-gray-400 transition-colors group-hover:text-gray-300">Custom AI training</span>
                </li>
                <li className="flex items-start gap-3 transition-transform group-hover:translate-x-1">
                  <Check className="h-5 w-5 text-pink-500 mt-0.5 transition-colors group-hover:text-pink-400" />
                  <span className="text-gray-400 transition-colors group-hover:text-gray-300">Custom integrations</span>
                </li>
                <li className="flex items-start gap-3 transition-transform group-hover:translate-x-1">
                  <Check className="h-5 w-5 text-pink-500 mt-0.5 transition-colors group-hover:text-pink-400" />
                  <span className="text-gray-400 transition-colors group-hover:text-gray-300">24/7 dedicated support</span>
                </li>
                <li className="flex items-start gap-3 transition-transform group-hover:translate-x-1">
                  <Check className="h-5 w-5 text-pink-500 mt-0.5 transition-colors group-hover:text-pink-400" />
                  <span className="text-gray-400 transition-colors group-hover:text-gray-300">SLA guarantees</span>
                </li>
              </ul>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
            <div className="divide-y divide-white/10">
              <FAQLine
                question="What is included in each plan?"
                answer="Each plan includes access to our core ticket automation features, integrations, and support. See the pricing cards above for details on ticket limits and advanced features."
              />
              <FAQLine
                question="Can I upgrade or downgrade later?"
                answer="Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades will apply at the end of your current billing cycle."
              />
              <FAQLine
                question="Is there a free trial?"
                answer="We offer a free trial for new users. Contact us to get started and explore the platform risk-free."
              />
              <FAQLine
                question="How do I contact support?"
                answer={<span>You can reach our support team anytime at <a href="mailto:business@ticcai.com" className="text-pink-400 underline">business@ticcai.com</a> or through the contact form on our website. Additionally, each customer will have a designated Slack channel where you can communicate directly and constantly with our team.</span>}
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <AuthModal isOpen={!!isAuthModalOpen} onClose={closeAuthModal} mode={isAuthModalOpen === 'signup' ? 'signup' : 'signin'} />
    </div>
  );
};

// FAQLine component (line-separated, not card)
function FAQLine({ question, answer }: { question: string; answer: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div>
      <button
        className="w-full flex items-center justify-between py-6 text-left group"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-lg text-white font-medium transition-all duration-300 group-hover:text-pink-500">{question}</span>
        <span className={`transition-transform duration-300 ${open ? '-rotate-180 text-pink-500' : 'rotate-0 text-gray-400 group-hover:text-pink-500'}`}>▼</span>
      </button>
      <div
        className={`grid transition-all duration-500 ease-in-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <div className={`transform transition-all duration-500 ease-out ${open ? 'translate-y-0 pt-4' : '-translate-y-2'}`}> 
            <p className="text-gray-400 transition-all duration-300">{answer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Animated particles background component
function ParticlesBackground() {
  const [particles, setParticles] = useState(() =>
    Array.from({ length: 28 }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 8 + 4,
      color: Math.random() > 0.5 ? '147, 197, 253' : '236, 72, 153',
      duration: Math.random() * 12 + 16,
      delay: Math.random() * 10,
    }))
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-[0.32] animate-float"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            left: `${p.left}%`,
            top: `${p.top}%`,
            background: `radial-gradient(circle at center, rgba(${p.color}, 0.8) 0%, rgba(${p.color}, 0.2) 50%, rgba(${p.color}, 0) 100%)`,
            boxShadow: `0 0 ${p.size * 2}px ${p.size / 2}px rgba(${p.color}, 0.3)`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            filter: 'blur(0.5px)',
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
}

export default Pricing; 